import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { PRODUCTION_PLANNING_TABS_CONFIG } from '../../common/PPSideBar/constants';
import { useSelector } from 'react-redux';
import { selectUserRoles } from '../../redux/UserReducer/selector';

export const useRBACTabs = () => {
  const userRoles = useSelector(selectUserRoles);
  const tabs = useMemo(() => {
    const processedTabs = [];
    Object.values(PRODUCTION_PLANNING_TABS_CONFIG).forEach((tab) => {
      // Check if user has access to this tab
      const hasAccess = userRoles.filter((role) => tab.roles.includes(role))
        .length > 0;
      
      if (hasAccess) {
        if (tab.isParent && tab.children) {
          // For parent items, check if user has access to any child
          const accessibleChildren = Object.values(tab.children).filter(
            (child) =>
              userRoles.filter((role) => child.roles.includes(role)).length > 0,
          );
          if (accessibleChildren.length > 0) {
            processedTabs.push({
              ...tab,
              children: accessibleChildren,
            });
          }
        } else if (!tab.isParent) {
          // Regular tab item
          processedTabs.push(tab);
        }
      }
    });
    return processedTabs;
  }, [userRoles]);

  const [activeTab, setActiveTab] = useState('');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Flatten tabs to find all URLs (including children)
    const allTabs = tabs.flatMap((tab) => {
      if (tab.isParent && tab.children) {
        return tab.children;
      }
      return tab;
    });
    
    const validTabPath = allTabs.find((tab) =>
      matchPath({ path: tab.url, end: false }, pathname),
    )?.url;
    
    if (activeTab === '' && !validTabPath) {
      const firstTab = allTabs.find((tab) => tab.url);
      if (firstTab?.url) {
        setActiveTab(firstTab.url);
        navigate(firstTab.url);
      }
    } else {
      setActiveTab(validTabPath);
    }
  }, [activeTab, pathname, navigate, setActiveTab, tabs]);

  return [tabs, activeTab, setActiveTab];
};
